import React, { FC } from 'react';
import { AdyenCheckout } from '@adyen/adyen-web';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import LoadingIndicator from 'src/components/LoadingIndicator';
import PaymentsFailure from 'src/components/Payments/Failure';
import PaymentsHeader from 'src/components/Payments/Header';
import { Container, LoadingOverlay, RootStyle } from 'src/components/Payments/style';
import useQueryParams from 'src/hooks/useQueryParams';
import { Routes } from 'src/router/route';
import { paymentsErrorAtom } from 'src/stores/payments/atoms';
import { EVENT_NAME, EVENT_TYPE, getErrorStatus } from 'src/types/Event';
import { adyenClientKey, getErrorCode, PaymentsCompleteRequest, paymentsEnvironment, PG_ERROR_CODE, PgPaymentMethod } from 'src/types/Payments';
import { pushEvent } from 'src/utils/event-gateway';

/**
 * Adyen 3DS 플로우로 인해 서버에서 중간에 리다이렉트시켜서 결제 진행하는 화면
 */
const AdyenRedirectView: FC = () => {
  const {
    sessionId,
    redirectResult,
    orderId
  } = useQueryParams<PaymentsCompleteRequest & {
    sessionId: string;
    redirectResult: string;
    productId: string;
  }>() || {};
  const [error, setError] = useAtom(paymentsErrorAtom);
  const router = useRouter();
  React.useEffect(() => {
    if (!sessionId || !orderId) return;
    const createCheckout = async () => {
      const checkout = await AdyenCheckout({
        environment: paymentsEnvironment,
        clientKey: adyenClientKey,
        session: {
          id: sessionId
        },
        onPaymentCompleted: result => {
          if (result.resultCode !== 'Authorised') {
            pushEvent({
              eventType: EVENT_TYPE.PAYMENT,
              eventName: EVENT_NAME.REDIRECT_DROP_IN_ERROR,
              eventParams: {
                payment_method: PgPaymentMethod.ADYEN,
                error_code: result.resultCode
              },
              options: {
                status: getErrorStatus(result.resultCode || '')
              }
            });
            setError({
              errorCode: PG_ERROR_CODE.ERROR
            });
            return;
          }
          router.replace(Routes['/payment/success']({
            queryParams: {
              orderId
            }
          }));
        },
        onPaymentFailed: result => {
          pushEvent({
            eventType: EVENT_TYPE.PAYMENT,
            eventName: EVENT_NAME.REDIRECT_DROP_IN_ERROR,
            eventParams: {
              payment_method: PgPaymentMethod.ADYEN,
              error_code: result?.resultCode
            },
            options: {
              status: getErrorStatus(result?.resultCode || '')
            }
          });
          setError({
            errorCode: PG_ERROR_CODE.ERROR
          });
        },
        onError: (e: Error) => {
          const errorCode = getErrorCode(e.name);
          pushEvent({
            eventType: EVENT_TYPE.PAYMENT,
            eventName: EVENT_NAME.REDIRECT_DROP_IN_ERROR,
            eventParams: {
              payment_method: PgPaymentMethod.ADYEN,
              error_code: e.name,
              error_message: e.message
            },
            options: {
              status: getErrorStatus(e.name)
            }
          });
          setError({
            errorCode
          });
        }
      });
      checkout.submitDetails({
        details: {
          redirectResult
        }
      }); // we finalize the redirect flow with the received payload
    };
    createCheckout();
  }, [sessionId, orderId, redirectResult, setError, router]);
  if (error) {
    return <PaymentsFailure errorCode={error.errorCode} />;
  }
  return <RootStyle data-sentry-element="RootStyle" data-sentry-component="AdyenRedirectView" data-sentry-source-file="AdyenRedirect.tsx">
      <PaymentsHeader data-sentry-element="PaymentsHeader" data-sentry-source-file="AdyenRedirect.tsx" />
      <Container data-sentry-element="Container" data-sentry-source-file="AdyenRedirect.tsx">
        <LoadingOverlay data-sentry-element="LoadingOverlay" data-sentry-source-file="AdyenRedirect.tsx">
          <LoadingIndicator size={30} data-sentry-element="LoadingIndicator" data-sentry-source-file="AdyenRedirect.tsx" />
        </LoadingOverlay>
      </Container>
    </RootStyle>;
};
export default AdyenRedirectView;